import { makeStyles } from 'tss-react/mui';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Tooltip,
  TooltipProps,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LaunchIcon from '@mui/icons-material/Launch';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BusinessIcon from '@mui/icons-material/Business';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import { StatusChip } from '../../../../components/StatusChip';
import TablePaginationLocalContainer from '../../../../components/Table/TablePaginationLocalContainer';
import TruncatedText from '../../../../components/TruncatedText';
import { AttorneysFeeStatusMap } from '../constants';
import {
  AttorneysFeeStatus,
  AccountingSyncStatus,
  DispositionStatus,
  PaymentMethodStatus,
  ProvisioningStatus,
} from '../../../../types';
import { FIRM_ROLES } from '../../../CasesView/types';
import { ROWS_PER_PAGE_OPTIONS, NETSUITE_LABEL } from '../../../../constants/constants';
import { getAccountingSyncStatusColor } from '../../../../helpers/getAccountingSyncStatusColor';
import {
  getVendorPaymentMethodDetails,
  type VendorPaymentMethodDetails,
} from '../../../../api/vendorApi/vendorAccountsApi';
import {
  getVendorDefaultPaymentAccountId,
  getVendorPaymentAccountOptions,
} from '../../../../helpers/vendorPaymentAccounts';

const useStyles = makeStyles<{ isLongData?: boolean }>()((theme: Theme, { isLongData }) => ({
  accordion: {
    backgroundColor: 'transparent',
    marginTop: theme.spacing(1),
    boxShadow: 'none',
    overflow: 'hidden',
    fontFamily: 'Poppins',
  },
  accordionSummary: {
    backgroundColor: theme.palette.primary.light,
    minHeight: '56px',
    padding: '0 16px',
    fontFamily: 'Poppins',
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '16px',
      color: theme.palette.common.white,
      fontFamily: 'Poppins',
    },
    flexDirection: 'row-reverse',
  },
  accordionDetails: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.light,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    fontFamily: 'Poppins',
  },
  tableContainer: {
    width: '99%',
    background: 'transparent',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    overflow: 'auto',
    fontSize: '14px',
    lineHeight: '17px',
    fontWeight: 500,
    fontFamily: 'Poppins',
    minHeight: isLongData ? '300px' : 'inherit',
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  tableHeaderCell: {
    background: theme.palette.primary.light,
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.text.secondary,
      borderBottom: '1px solid #515151',
      fontWeight: 500,
      fontFamily: 'Poppins',
      fontSize: '14px',
      height: '35px',
      padding: '16px 12px 16px 8px',
      whiteSpace: 'nowrap',
    },
  },
  tableBodyCell: {
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.common.white,
      borderBottom: '1px solid #4A4E5D',
      fontWeight: 400,
      fontFamily: 'Poppins',
      fontSize: '14px',
      verticalAlign: 'top',
      padding: '16px 12px 16px 8px',
    },
  },
  // Column widths Aligns with DetailsDeductionTable
  columnName: {
    width: '210px',
    minWidth: '210px !important',
    maxWidth: '210px !important',
    whiteSpace: 'normal',
    overflow: 'hidden',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  columnAmount: {
    width: '110px',
    minWidth: '110px !important',
    maxWidth: '110px !important',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  columnIntent: {
    width: '90px',
    minWidth: '90px !important',
    maxWidth: '90px !important',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  columnPaymentMethod: {
    width: '130px',
    minWidth: '130px !important',
    maxWidth: '130px !important',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  columnPayAccount: {
    width: '320px',
    minWidth: '320px !important',
    maxWidth: '320px !important',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  columnStatus: {
    width: '170px',
    minWidth: '170px !important',
    maxWidth: '170px !important',
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
  },
  columnAction: {
    width: '96px',
    minWidth: '96px !important',
    maxWidth: '96px !important',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  stopDialogPaper: {
    backgroundColor: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    minWidth: '420px',
  },
  retryDialogPaper: {
    backgroundColor: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    width: 'min(560px, calc(100vw - 32px))',
    minWidth: '0',
    maxWidth: 'calc(100vw - 32px)',
    boxSizing: 'border-box',
    overflowX: 'hidden',
  },
  dialogContainer: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.primary.light,
      border: `1px solid ${theme.palette.secondary.dark}`,
      borderRadius: '12px',
    },
    '& .MuiDialogTitle-root': {
      color: theme.palette.common.white,
      borderBottom: `1px solid ${theme.palette.secondary.dark}`,
    },
    '& .MuiDialogContent-root': {
      backgroundColor: theme.palette.primary.light,
    },
    '& .MuiDialogActions-root': {
      backgroundColor: theme.palette.primary.light,
      borderTop: `1px solid ${theme.palette.secondary.dark}`,
    },
  },
  dialogTitle: {
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '20px',
    borderBottom: `1px solid ${theme.palette.secondary.dark}`,
  },
  stopDialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: `${theme.spacing(2)} !important`,
  },
  retryDialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: `${theme.spacing(2)} !important`,
    overflowY: 'visible',
    overflowX: 'hidden',
    width: '100%',
    boxSizing: 'border-box',
  },
  stopDialogActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.secondary.dark}`,
  },
  retryDialogActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.secondary.dark}`,
    flexWrap: 'nowrap',
    width: '100%',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    '& .MuiButton-root': {
      whiteSpace: 'nowrap',
      flexShrink: 0,
    },
    '& > :not(style) ~ :not(style)': {
      marginLeft: '0 !important',
    },
    '@media (max-width: 640px)': {
      flexWrap: 'wrap',
    },
  },
  formControl: {
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontFamily: 'Inter',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.info.main,
    },
    '& .MuiOutlinedInput-root': {
      color: theme.palette.common.white,
      fontFamily: 'Inter',
      '& fieldset': {
        borderColor: theme.palette.secondary.dark,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.info.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.info.main,
      },
    },
  },
  menuItem: {
    fontFamily: 'Inter',
    fontSize: '14px',
    color: theme.palette.common.white,
  },
  helperText: {
    color: '#FF8663',
    fontFamily: 'Inter',
    fontSize: '12px',
    marginTop: theme.spacing(-1),
  },
  secondaryButton: {
    color: theme.palette.common.white,
    borderColor: theme.palette.secondary.dark,
    minHeight: '34px',
    height: '34px',
    padding: '6px 14px',
    borderRadius: '6px',
    textTransform: 'none',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 500,
  },
  primaryButton: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.common.white,
    minHeight: '34px',
    height: '34px',
    padding: '6px 14px',
    borderRadius: '6px',
    textTransform: 'none',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: theme.palette.info.main,
    },
  },
}));

const STOP_CHECK_REASON_MAP: Record<string, string> = {
  'Sent to the wrong address': 'INCORRECT_DESTINATION',
  'Check has not arrived / appears lost': 'LOST_CHECK',
  'Incorrect payee name': 'INCORRECTLY_CREATED',
  'Incorrect amount': 'INCORRECT_AMOUNT',
  'I want a different payout method': 'ON_USER_REQUEST',
  'Other': 'OTHERS',
};

const STOP_COMMENTS_MAX_LENGTH = 500;

// The atty-fee retry-with-update API requires a payout account, so only account-based methods apply.
const RETRY_ACCOUNT_METHODS = ['ACH', 'CHECK'];
// Matches DetailsDeductionTable's non-expense table width.
const TABLE_MIN_WIDTH = 1180;

const entityTooltipProps: TooltipProps['componentsProps'] = {
  tooltip: {
    sx: (theme) => ({
      backgroundColor: theme.palette.common.white,
      color: theme.palette.primary.light,
      borderRadius: '5px',
      boxShadow: '3px',
      fontFamily: 'Inter',
      fontSize: '13px',
      fontWeight: 500,
      padding: '5px',
      maxWidth: '140px',
    }),
  },
};

const memoTooltipProps: TooltipProps['componentsProps'] = {
  tooltip: {
    sx: (theme) => ({
      backgroundColor: theme.palette.common.white,
      color: theme.palette.primary.light,
      borderRadius: '5px',
      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.35)',
      fontFamily: 'Inter',
      fontSize: '13px',
      fontWeight: 500,
      lineHeight: 1.45,
      padding: '8px 12px',
      maxWidth: '280px',
      whiteSpace: 'pre-line',
      wordBreak: 'break-word',
    }),
  },
  arrow: {
    sx: (theme) => ({ color: theme.palette.common.white }),
  },
};

type AttorneyFirmField = {
  id: number | string;
  name: string;
  amount: string;
  status: AttorneysFeeStatus;
  role?: string;
  counselEntityType?: 'FIRM' | 'VENDOR';
  paymentMethod?: PaymentMethodStatus;
  disposition?: DispositionStatus;
  attyFeeId?: number | null;
  vendorParticipationId?: number | null;
  paymentAccountId?: number | null;
  paymentAccountName?: string | null;
  memo?: string | null;
  canRetryVendorPayment?: boolean;
  canStopDeductionTransaction?: boolean;
  paymentErrorMessage?: string | null;
  accounting?: {
    billError: string;
    billLink: string;
    billSyncStatus?: AccountingSyncStatus | null;
    billPaymentError: string;
    billPaymentLink: string;
    billPaymentSyncStatus?: AccountingSyncStatus | null;
    vendorLink?: string;
  };
};

interface AttorneyFeesTableProps {
  fields: AttorneyFirmField[];
  secondaryFirmsData: { id: string; name: string; role: string }[] | unknown;
  leadFirmData?: { id: string; name: string } | unknown;
  onRetryVendorPayment?: (attyFeeId: number) => void;
  onRetryVendorPaymentWithUpdate?: (
    attyFeeId: number,
    vendorPaymentAccountId: number,
    paymentMethod: string,
    vendorPaymentAccountName?: string | null,
  ) => void;
  onStopAttyFeeTransaction?: (
    attyFeeId: number,
    reason: string,
    additionalComments: string,
  ) => Promise<boolean>;
}

const DetailsAttorneysFees = ({
  fields,
  onRetryVendorPayment,
  onRetryVendorPaymentWithUpdate,
  onStopAttyFeeTransaction,
}: AttorneyFeesTableProps) => {
  const attorneyFeesFields = fields;
  const isLongData = attorneyFeesFields?.length > 3;
  const styles = useStyles({ isLongData });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const paginatedFields = Array.isArray(attorneyFeesFields)
    ? attorneyFeesFields.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  const [isStopDialogOpen, setIsStopDialogOpen] = useState(false);
  const [isStopSubmitting, setIsStopSubmitting] = useState(false);
  const [selectedStopReasonLabel, setSelectedStopReasonLabel] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [showStopReasonError, setShowStopReasonError] = useState(false);
  const [showAdditionalCommentsError, setShowAdditionalCommentsError] = useState(false);
  const [selectedStopAttyFeeId, setSelectedStopAttyFeeId] = useState<number | null>(null);
  const [isRetryOptionsDialogOpen, setIsRetryOptionsDialogOpen] = useState(false);
  const [retryDialogMode, setRetryDialogMode] = useState<'options' | 'differentOption'>('options');
  const [selectedRetryFee, setSelectedRetryFee] = useState<AttorneyFirmField | null>(null);
  const [isRetryOverrideLoading, setIsRetryOverrideLoading] = useState(false);
  const [retryMethodOptions, setRetryMethodOptions] = useState<Array<{ id: string; label: string }>>(
    [],
  );
  const [retryPaymentMethod, setRetryPaymentMethod] = useState('');
  const [retryAccountOptions, setRetryAccountOptions] = useState<Array<{ id: string; label: string }>>(
    [],
  );
  const [retryPaymentAccountId, setRetryPaymentAccountId] = useState('');
  const [retryVendorPaymentDetails, setRetryVendorPaymentDetails] =
    useState<VendorPaymentMethodDetails | null>(null);
  const [retryOverrideError, setRetryOverrideError] = useState('');

  const stopReasonOptions = Object.keys(STOP_CHECK_REASON_MAP);
  const darkSelectMenuProps = {
    PaperProps: {
      sx: {
        backgroundColor: 'primary.light',
        border: (theme: Theme) => `1px solid ${theme.palette.secondary.dark}`,
        '& .MuiMenuItem-root': {
          color: 'common.white',
        },
      },
    },
  };

  // Atty fee vendor actions only apply to VENDOR counsel rows that carry an attyFeeId.
  const isVendorAttyFee = (field: AttorneyFirmField) =>
    field.counselEntityType === 'VENDOR' && !!field.attyFeeId;

  // Check memo icon only applies to VENDOR counsel rows paid by CHECK.
  const isVendorCheckPayment = (field: AttorneyFirmField) =>
    field.counselEntityType === 'VENDOR' &&
    (field.paymentMethod || '').toUpperCase() === 'CHECK' &&
    (field.disposition || '').toUpperCase() === 'PAY';

  const renderCheckMemoIcon = (memo?: string | null) => {
    const memoText = (memo || '').trim();
    const hasMemo = memoText.length > 0;

    return (
      <Tooltip
        arrow
        placement="bottom"
        componentsProps={memoTooltipProps}
        title={
          <Box>
            <Box
              display="flex"
              alignItems="center"
              gap="4px"
              mb="4px"
              sx={{ fontSize: '12px', fontWeight: 600, color: '#6B6F82' }}
            >
              <StickyNote2OutlinedIcon sx={{ fontSize: '16px' }} />
              Check memo
            </Box>
            {hasMemo ? memoText : 'No memo added'}
          </Box>
        }
      >
        <StickyNote2OutlinedIcon
          tabIndex={0}
          aria-label={hasMemo ? 'View check memo' : 'No memo added'}
          sx={{
            fontSize: '20px',
            color: 'common.white',
            cursor: 'pointer',
            flexShrink: 0,
            opacity: hasMemo ? 0.85 : 0.35,
            '&:hover, &:focus-visible': { opacity: hasMemo ? 1 : 0.35 },
          }}
        />
      </Tooltip>
    );
  };

  const handleOpenStopDialog = (attyFeeId: number) => {
    setSelectedStopAttyFeeId(attyFeeId);
    setIsStopDialogOpen(true);
  };

  const handleCloseStopDialog = () => {
    setIsStopDialogOpen(false);
    setSelectedStopAttyFeeId(null);
    setSelectedStopReasonLabel('');
    setAdditionalComments('');
    setShowStopReasonError(false);
    setShowAdditionalCommentsError(false);
  };

  const handleSubmitStopTransaction = async () => {
    const selectedReasonCode = STOP_CHECK_REASON_MAP[selectedStopReasonLabel];

    if (!selectedReasonCode) {
      setShowStopReasonError(true);
      return;
    }

    if (selectedStopReasonLabel === 'Other' && !additionalComments.trim()) {
      setShowAdditionalCommentsError(true);
      return;
    }

    if (!selectedStopAttyFeeId || !onStopAttyFeeTransaction) {
      handleCloseStopDialog();
      return;
    }

    // The stop API waits until Passport reports STOPPED, so keep the dialog open with a spinner.
    setIsStopSubmitting(true);
    try {
      const isStopped = await onStopAttyFeeTransaction(
        selectedStopAttyFeeId,
        selectedReasonCode,
        additionalComments.trim(),
      );
      if (isStopped) {
        handleCloseStopDialog();
      }
    } finally {
      setIsStopSubmitting(false);
    }
  };

  const handleOpenRetryOptionsDialog = (fee: AttorneyFirmField) => {
    setSelectedRetryFee(fee);
    setRetryDialogMode('options');
    setIsRetryOptionsDialogOpen(true);
  };

  const handleCloseRetryOptionsDialog = () => {
    setIsRetryOptionsDialogOpen(false);
    setRetryDialogMode('options');
    setSelectedRetryFee(null);
    setRetryOverrideError('');
    setRetryMethodOptions([]);
    setRetryPaymentMethod('');
    setRetryAccountOptions([]);
    setRetryPaymentAccountId('');
    setRetryVendorPaymentDetails(null);
  };

  const getRetryVendorId = (fee: AttorneyFirmField) =>
    Number(fee.vendorParticipationId ?? fee.id) || null;

  const handleRetrySameData = () => {
    if (!selectedRetryFee?.attyFeeId || !onRetryVendorPayment) {
      handleCloseRetryOptionsDialog();
      return;
    }

    const attyFeeId = selectedRetryFee.attyFeeId;

    const validateAndRetrySameData = async () => {
      setIsRetryOverrideLoading(true);
      setRetryOverrideError('');

      try {
        const vendorId = getRetryVendorId(selectedRetryFee);

        if (!vendorId) {
          setRetryOverrideError('Vendor information is not available for this attorney fee.');
          return;
        }

        const paymentDetailsResponse = await getVendorPaymentMethodDetails(vendorId);
        const vendorDetails = paymentDetailsResponse.data;
        setRetryVendorPaymentDetails(vendorDetails);

        const paymentMethod = (selectedRetryFee.paymentMethod || '').toUpperCase();
        const requiresAccount = RETRY_ACCOUNT_METHODS.includes(paymentMethod);

        if (requiresAccount) {
          const paymentAccountId = selectedRetryFee.paymentAccountId
            ? String(selectedRetryFee.paymentAccountId)
            : '';

          if (!paymentAccountId) {
            setRetryOverrideError('Payment account is required to retry with the same option.');
            return;
          }

          const selectedAccount =
            paymentMethod === 'ACH'
              ? vendorDetails.achAccounts?.find((account) => String(account.id) === paymentAccountId)
              : vendorDetails.checkAccounts?.find(
                  (account) => String(account.id) === paymentAccountId,
                );

          if (!selectedAccount) {
            setRetryOverrideError('The selected payment account is no longer available.');
            return;
          }

          if (selectedAccount.provisionStatus !== ProvisioningStatus.Completed) {
            setRetryOverrideError(
              'Selected payment account is not fully provisioned. Please choose an account with Completed status.',
            );
            return;
          }
        }

        onRetryVendorPayment(attyFeeId);
        handleCloseRetryOptionsDialog();
      } catch {
        setRetryOverrideError('Unable to validate vendor payment account for retry.');
      } finally {
        setIsRetryOverrideLoading(false);
      }
    };

    validateAndRetrySameData();
  };

  const handleRetryOtherMethod = async () => {
    if (!selectedRetryFee?.attyFeeId || !onRetryVendorPaymentWithUpdate) {
      handleCloseRetryOptionsDialog();
      return;
    }

    setIsRetryOverrideLoading(true);
    setRetryOverrideError('');

    try {
      const vendorId = getRetryVendorId(selectedRetryFee);

      if (!vendorId) {
        setRetryOverrideError('Vendor information is not available for this attorney fee.');
        return;
      }

      const paymentDetailsResponse = await getVendorPaymentMethodDetails(vendorId);
      const vendorDetails = paymentDetailsResponse.data;
      setRetryVendorPaymentDetails(vendorDetails);

      const methodOptions = (vendorDetails.activePaymentMethod || [])
        .filter((method) => RETRY_ACCOUNT_METHODS.includes(method))
        .map((method) => ({ id: method, label: method }));

      if (!methodOptions.length) {
        setRetryOverrideError('No active vendor payment methods are available for retry.');
        return;
      }

      setRetryMethodOptions(methodOptions);

      const currentMethod = (selectedRetryFee.paymentMethod || '').toUpperCase();
      const defaultMethod = methodOptions.some((option) => option.id === currentMethod)
        ? currentMethod
        : methodOptions[0]?.id || '';

      setRetryPaymentMethod(defaultMethod);
      setRetryDialogMode('differentOption');
    } catch {
      setRetryOverrideError('Unable to load vendor payment methods for retry.');
    } finally {
      setIsRetryOverrideLoading(false);
    }
  };

  const getFormattedAccountOptions = (details: VendorPaymentMethodDetails, method: string) => {
    return getVendorPaymentAccountOptions(details, method).map((item) => {
      const subtitleWithoutAccountType =
        item.paymentMethod === 'ACH' ? item.subtitle.split(' ').slice(-1).join(' ') : item.subtitle;

      return {
        id: item.id,
        label: subtitleWithoutAccountType ? `${item.label} - ${subtitleWithoutAccountType}` : item.label,
      };
    });
  };

  const handleRetryWithUpdatedData = () => {
    if (!selectedRetryFee?.attyFeeId || !onRetryVendorPaymentWithUpdate) {
      handleCloseRetryOptionsDialog();
      return;
    }

    if (!retryPaymentMethod) {
      setRetryOverrideError('Payment method is required.');
      return;
    }

    if (!retryPaymentAccountId) {
      setRetryOverrideError('Payment account is required.');
      return;
    }

    const selectedAccount =
      retryPaymentMethod === 'ACH'
        ? retryVendorPaymentDetails?.achAccounts?.find(
            (account) => String(account.id) === retryPaymentAccountId,
          )
        : retryVendorPaymentDetails?.checkAccounts?.find(
            (account) => String(account.id) === retryPaymentAccountId,
          );

    if (!selectedAccount) {
      setRetryOverrideError('The selected payment account is no longer available.');
      return;
    }

    if (selectedAccount.provisionStatus !== ProvisioningStatus.Completed) {
      setRetryOverrideError(
        'Selected payment account is not fully provisioned. Please choose an account with Completed status.',
      );
      return;
    }

    const selectedAccountOption = retryAccountOptions.find(
      (option) => option.id === retryPaymentAccountId,
    );

    onRetryVendorPaymentWithUpdate(
      selectedRetryFee.attyFeeId,
      Number(retryPaymentAccountId),
      retryPaymentMethod,
      selectedAccountOption?.label || null,
    );

    handleCloseRetryOptionsDialog();
  };

  useEffect(() => {
    if (!retryVendorPaymentDetails || !retryPaymentMethod) {
      setRetryAccountOptions([]);
      setRetryPaymentAccountId('');
      return;
    }

    const accountOptions = getFormattedAccountOptions(retryVendorPaymentDetails, retryPaymentMethod);
    setRetryAccountOptions(accountOptions);

    setRetryPaymentAccountId((previous) => {
      if (accountOptions.some((option) => option.id === previous)) {
        return previous;
      }

      const currentAccountId = selectedRetryFee?.paymentAccountId
        ? String(selectedRetryFee.paymentAccountId)
        : '';
      if (accountOptions.some((option) => option.id === currentAccountId)) {
        return currentAccountId;
      }

      return getVendorDefaultPaymentAccountId(retryVendorPaymentDetails, retryPaymentMethod) || '';
    });
  }, [retryVendorPaymentDetails, retryPaymentMethod, selectedRetryFee?.paymentAccountId]);

  return (
    <Accordion defaultExpanded className={styles.classes.accordion}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
        className={styles.classes.accordionSummary}
      >
        <Typography>Attorney Fees</Typography>
      </AccordionSummary>
      <AccordionDetails className={styles.classes.accordionDetails}>
        <>
          <TableContainer className={styles.classes.tableContainer} component={Paper}>
            <Table
              sx={{
                width: `max(100%, ${TABLE_MIN_WIDTH}px)`,
                minWidth: TABLE_MIN_WIDTH,
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
              }}
              size="small"
            >
              <TableHead className={styles.classes.tableHeaderCell}>
                <TableRow>
                  <TableCell key={'Name'} className={styles.classes.columnName}>{'Name'}</TableCell>
                  <TableCell key={'Amount'} className={styles.classes.columnAmount}>{'Amount'}</TableCell>
                  <TableCell key={'Intent'} className={styles.classes.columnIntent}>{'Intent'}</TableCell>
                  <TableCell key={'PaymentMethod'} className={styles.classes.columnPaymentMethod}>
                    {'Payment Method'}
                  </TableCell>
                  <TableCell key={'PayAccount'} className={styles.classes.columnPayAccount}>
                    {'PAY Account'}
                  </TableCell>
                  <TableCell key={'Status'} className={styles.classes.columnStatus}>{'Status'}</TableCell>
                  <TableCell key={'Action'} className={styles.classes.columnAction}>{'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody className={clsx(styles.classes.tableBodyCell)}>
                {paginatedFields.length > 0 ? (
                  paginatedFields.map(
                    (field) => (
                      <TableRow key={`${field.counselEntityType ?? 'FIRM'}-${field.id}`}>
                        <TableCell className={styles.classes.columnName}>
                          <Box display="flex" alignItems="center" gap="8px" overflow="hidden">
                            <Tooltip
                              title={field.counselEntityType === 'VENDOR' ? 'Vendor' : 'Firm'}
                              arrow
                              componentsProps={entityTooltipProps}
                            >
                              {field.counselEntityType === 'VENDOR' ? (
                                <StorefrontIcon
                                  sx={{ fontSize: '20px', color: 'common.white', flexShrink: 0 }}
                                />
                              ) : (
                                <BusinessIcon
                                  sx={{ fontSize: '20px', color: 'common.white', flexShrink: 0 }}
                                />
                              )}
                            </Tooltip>
                            <TruncatedText text={field.name || '-'} maxLength={30} />
                            {field.accounting?.vendorLink != null && (
                              <Tooltip
                                title={field.counselEntityType === 'VENDOR' ? `View Vendor in ${NETSUITE_LABEL}` : `View Firm in ${NETSUITE_LABEL}`}
                                arrow
                                componentsProps={{
                                  tooltip: {
                                    sx: (theme) => ({
                                      backgroundColor: theme.palette.common.white,
                                      color: theme.palette.primary.light,
                                      borderRadius: '5px',
                                      boxShadow: '3px',
                                      fontFamily: 'Inter',
                                      fontSize: '13px',
                                      fontWeight: 500,
                                      padding: '5px',
                                      maxWidth: '140px',
                                    }),
                                  },
                                }}
                              >
                                <LaunchIcon
                                  sx={{
                                    cursor: 'pointer',
                                    color: 'common.white',
                                    fontSize: '20px',
                                    flexShrink: 0,
                                  }}
                                  onClick={() =>
                                    window.open(field.accounting?.vendorLink || '', '_blank')
                                  }
                                />
                              </Tooltip>
                            )}
                          </Box>
                          {field.role && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{ display: 'block', fontSize: '12px' }}
                            >
                              {FIRM_ROLES[field.role as keyof typeof FIRM_ROLES]}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell className={styles.classes.columnAmount}>
                          <Typography>{(field.amount) || '-'}</Typography>
                        </TableCell>
                        <TableCell className={styles.classes.columnIntent}>
                          {Number(field.amount) !== 0 ? field.disposition : '-'}
                        </TableCell>
                        <TableCell className={styles.classes.columnPaymentMethod}>
                          {Number(field.amount) !== 0 ? (
                            <Box display="inline-flex" alignItems="center" gap="6px">
                              {field.paymentMethod}
                              {isVendorCheckPayment(field) && renderCheckMemoIcon(field.memo)}
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className={styles.classes.columnPayAccount}>
                          {field.counselEntityType === 'VENDOR' &&
                          Number(field.amount) !== 0 &&
                          ['PAY', 'Pay'].includes(field.disposition || '')
                            ? field.paymentAccountName || '-'
                            : '-'}
                        </TableCell>
                        <TableCell className={styles.classes.columnStatus}>
                          {Number(field.amount) !== 0 ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              {field.status &&
                                (() => {
                                  const statusChip = (
                                    <StatusChip
                                      label={
                                        AttorneysFeeStatusMap[field.status as AttorneysFeeStatus]
                                          ?.label || field.status
                                      }
                                      color={
                                        AttorneysFeeStatusMap[field.status as AttorneysFeeStatus]
                                          ?.color || 'default'
                                      }
                                    />
                                  );
                                  return field.paymentErrorMessage ? (
                                    <Tooltip title={field.paymentErrorMessage} arrow>
                                      <span>{statusChip}</span>
                                    </Tooltip>
                                  ) : (
                                    statusChip
                                  );
                                })()}

                              {(field.accounting?.billLink ||
                                field.accounting?.billError ||
                                field.accounting?.billSyncStatus) &&
                                (field.accounting?.billLink || field.accounting?.billError ? (
                                  <Tooltip
                                    arrow
                                    componentsProps={{
                                      tooltip: {
                                        sx: (theme) => ({
                                          backgroundColor: theme.palette.common.white,
                                          color: theme.palette.primary.light,
                                          borderRadius: '5px',
                                          boxShadow: '3px',
                                          fontFamily: 'Inter',
                                          fontSize: '13px',
                                          fontWeight: 500,
                                          padding: '5px',
                                          maxWidth: '180px',
                                        }),
                                      },
                                    }}
                                    title={field.accounting?.billError || 'View Bill'}
                                  >
                                    <Box
                                      sx={{
                                        paddingTop: '5px',
                                        cursor: field.accounting?.billLink ? 'pointer' : 'default',
                                      }}
                                      onClick={
                                        field.accounting?.billLink
                                          ? () => window.open(field.accounting?.billLink, '_blank')
                                          : undefined
                                      }
                                    >
                                      <ReceiptIcon
                                        fontSize="small"
                                        sx={{
                                          color: getAccountingSyncStatusColor(
                                            field.accounting?.billSyncStatus,
                                          ),
                                        }}
                                      />
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <Box sx={{ paddingTop: '5px' }}>
                                    <ReceiptIcon
                                      fontSize="small"
                                      sx={{
                                        color: getAccountingSyncStatusColor(
                                          field.accounting?.billSyncStatus,
                                        ),
                                      }}
                                    />
                                  </Box>
                                ))}

                              {(field.accounting?.billPaymentLink ||
                                field.accounting?.billPaymentError ||
                                field.accounting?.billPaymentSyncStatus) &&
                                (field.accounting?.billPaymentLink ||
                                field.accounting?.billPaymentError ? (
                                  <Tooltip
                                    arrow
                                    componentsProps={{
                                      tooltip: {
                                        sx: (theme) => ({
                                          backgroundColor: theme.palette.common.white,
                                          color: theme.palette.primary.light,
                                          borderRadius: '5px',
                                          boxShadow: '3px',
                                          fontFamily: 'Inter',
                                          fontSize: '13px',
                                          fontWeight: 500,
                                          padding: '5px',
                                          maxWidth: '180px',
                                        }),
                                      },
                                    }}
                                    title={
                                      field.accounting?.billPaymentError || 'View Bill Payment'
                                    }
                                  >
                                    <Box
                                      sx={{
                                        paddingTop: '5px',
                                        cursor: field.accounting?.billPaymentLink
                                          ? 'pointer'
                                          : 'default',
                                      }}
                                      onClick={
                                        field.accounting?.billPaymentLink
                                          ? () =>
                                              window.open(
                                                field.accounting?.billPaymentLink,
                                                '_blank',
                                              )
                                          : undefined
                                      }
                                    >
                                      <PaymentsIcon
                                        fontSize="small"
                                        sx={{
                                          color: getAccountingSyncStatusColor(
                                            field.accounting?.billPaymentSyncStatus,
                                          ),
                                        }}
                                      />
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <Box sx={{ paddingTop: '5px' }}>
                                    <PaymentsIcon
                                      fontSize="small"
                                      sx={{
                                        color: getAccountingSyncStatusColor(
                                          field.accounting?.billPaymentSyncStatus,
                                        ),
                                      }}
                                    />
                                  </Box>
                                ))}
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className={styles.classes.columnAction}>
                          {isVendorAttyFee(field) ? (
                            <Box display="flex" gap={0.5} alignItems="center">
                              {field.canRetryVendorPayment && onRetryVendorPayment && (
                                <Tooltip title="Retry Vendor Payment" arrow>
                                  <IconButton
                                    aria-label="Retry Vendor Payment"
                                    size="small"
                                    onClick={() => handleOpenRetryOptionsDialog(field)}
                                    sx={{ color: 'common.white' }}
                                  >
                                    <RefreshIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {field.canStopDeductionTransaction && onStopAttyFeeTransaction && (
                                <Tooltip title="Stop Transaction" arrow>
                                  <IconButton
                                    aria-label="Stop Transaction"
                                    size="small"
                                    onClick={() => handleOpenStopDialog(Number(field.attyFeeId))}
                                    sx={{ color: '#FF8663' }}
                                  >
                                    <ReportGmailerrorredIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ),
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ textAlign: 'center !important' }}>
                      <Typography variant="body2" color="textSecondary">
                        No Attorney Fee records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePaginationLocalContainer
            count={Array.isArray(attorneyFeesFields) ? attorneyFeesFields.length : 0}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value as string, ROWS_PER_PAGE_OPTIONS[0]));
              setPage(0);
            }}
            totalPages={
              Array.isArray(attorneyFeesFields)
                ? Math.ceil(attorneyFeesFields.length / rowsPerPage)
                : 0
            }
          />

          <Dialog
            open={isStopDialogOpen}
            onClose={isStopSubmitting ? undefined : handleCloseStopDialog}
            className={styles.classes.dialogContainer}
            classes={{ paper: styles.classes.stopDialogPaper }}
          >
            <DialogTitle className={styles.classes.dialogTitle}>
              Stop Attorney Fee Transaction
            </DialogTitle>
            <DialogContent className={styles.classes.stopDialogContent}>
              <FormControl fullWidth className={styles.classes.formControl}>
                <InputLabel id="stop-atty-fee-reason-label">Reason *</InputLabel>
                <Select
                  labelId="stop-atty-fee-reason-label"
                  label="Reason *"
                  value={selectedStopReasonLabel}
                  MenuProps={darkSelectMenuProps}
                  disabled={isStopSubmitting}
                  onChange={(event) => {
                    setSelectedStopReasonLabel(event.target.value as string);
                    if (showStopReasonError) {
                      setShowStopReasonError(false);
                    }
                    if (showAdditionalCommentsError) {
                      setShowAdditionalCommentsError(false);
                    }
                  }}
                >
                  {stopReasonOptions.map((option) => (
                    <MenuItem key={option} value={option} className={styles.classes.menuItem}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {showStopReasonError && (
                <Typography className={styles.classes.helperText}>Reason is required.</Typography>
              )}

              <TextField
                label="Additional comments"
                placeholder={selectedStopReasonLabel === 'Other' ? 'Required' : 'Optional'}
                multiline
                minRows={3}
                value={additionalComments}
                disabled={isStopSubmitting}
                inputProps={{ maxLength: STOP_COMMENTS_MAX_LENGTH }}
                helperText={`${additionalComments.length}/${STOP_COMMENTS_MAX_LENGTH}`}
                FormHelperTextProps={{ sx: { textAlign: 'right', color: 'text.secondary' } }}
                onChange={(event) => {
                  setAdditionalComments(event.target.value);
                  if (showAdditionalCommentsError && event.target.value.trim()) {
                    setShowAdditionalCommentsError(false);
                  }
                }}
                className={styles.classes.formControl}
                fullWidth
              />

              {showAdditionalCommentsError && (
                <Typography className={styles.classes.helperText}>
                  Additional comments are required when selecting &quot;Other&quot;.
                </Typography>
              )}
            </DialogContent>
            <DialogActions className={styles.classes.stopDialogActions}>
              <Button
                variant="outlined"
                className={styles.classes.secondaryButton}
                onClick={handleCloseStopDialog}
                disabled={isStopSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className={styles.classes.primaryButton}
                onClick={handleSubmitStopTransaction}
                disabled={isStopSubmitting}
                startIcon={
                  isStopSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined
                }
              >
                {isStopSubmitting ? 'Stopping...' : 'Stop Transaction'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={isRetryOptionsDialogOpen}
            onClose={isRetryOverrideLoading ? undefined : handleCloseRetryOptionsDialog}
            className={styles.classes.dialogContainer}
            classes={{ paper: styles.classes.retryDialogPaper }}
          >
            <DialogTitle className={styles.classes.dialogTitle}>Retry Vendor Payment</DialogTitle>
            <DialogContent className={styles.classes.retryDialogContent}>
              {retryDialogMode === 'options' ? (
                <>
                  <Typography color="white" fontFamily="Inter" fontSize="14px">
                    Choose how you want to retry this vendor payment.
                  </Typography>
                  {isRetryOverrideLoading && (
                    <Box display="flex" alignItems="center" gap={1} py={1}>
                      <CircularProgress size={18} />
                      <Typography color="white" fontFamily="Inter" fontSize="13px">
                        Loading payment options...
                      </Typography>
                    </Box>
                  )}
                  {retryOverrideError && (
                    <Typography className={styles.classes.helperText}>
                      {retryOverrideError}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <FormControl fullWidth className={styles.classes.formControl}>
                    <InputLabel id="retry-atty-fee-method-label">Payment Method *</InputLabel>
                    <Select
                      labelId="retry-atty-fee-method-label"
                      label="Payment Method *"
                      value={retryPaymentMethod}
                      MenuProps={darkSelectMenuProps}
                      onChange={(event) => {
                        setRetryPaymentMethod(event.target.value as string);
                        setRetryOverrideError('');
                      }}
                    >
                      {retryMethodOptions.map((option) => (
                        <MenuItem
                          key={option.id}
                          value={option.id}
                          className={styles.classes.menuItem}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth className={styles.classes.formControl}>
                    <InputLabel id="retry-atty-fee-account-label">Payment Account *</InputLabel>
                    <Select
                      labelId="retry-atty-fee-account-label"
                      label="Payment Account *"
                      value={retryPaymentAccountId}
                      MenuProps={darkSelectMenuProps}
                      onChange={(event) => {
                        setRetryPaymentAccountId(event.target.value as string);
                        setRetryOverrideError('');
                      }}
                    >
                      {retryAccountOptions.map((option) => (
                        <MenuItem
                          key={option.id}
                          value={option.id}
                          className={styles.classes.menuItem}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {retryOverrideError && (
                    <Typography className={styles.classes.helperText}>
                      {retryOverrideError}
                    </Typography>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions className={styles.classes.retryDialogActions}>
              <Button
                variant="outlined"
                className={styles.classes.secondaryButton}
                onClick={handleCloseRetryOptionsDialog}
                disabled={isRetryOverrideLoading}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                className={styles.classes.secondaryButton}
                onClick={handleRetrySameData}
                disabled={isRetryOverrideLoading}
              >
                Retry With Same Option
              </Button>
              {retryDialogMode === 'options' ? (
                <Button
                  variant="contained"
                  className={styles.classes.primaryButton}
                  onClick={handleRetryOtherMethod}
                  disabled={isRetryOverrideLoading}
                >
                  Retry With Different Option
                </Button>
              ) : (
                <Button
                  variant="contained"
                  className={styles.classes.primaryButton}
                  disabled={isRetryOverrideLoading}
                  onClick={handleRetryWithUpdatedData}
                >
                  Retry Payment
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </>
      </AccordionDetails>
    </Accordion>
  );
};

export default DetailsAttorneysFees;
